import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["display", "form"]
  static values = {
    url: String,
    method: { type: String, default: "patch" }
  }

  edit() {
    this.displayTarget.classList.add("hidden")
    this.formTarget.classList.remove("hidden")
    const firstInput = this.formTarget.querySelector("input[type=text]")
    if (firstInput) {
      firstInput.focus()
      firstInput.select()
    }
  }

  cancel() {
    this.formTarget.classList.add("hidden")
    this.displayTarget.classList.remove("hidden")
    // Reset form values to original
    this.formTarget.querySelectorAll("input[type=text]").forEach(input => {
      input.value = input.defaultValue
    })
    this.formTarget.querySelectorAll("textarea").forEach(ta => {
      ta.value = ta.defaultValue
    })
  }

  keydown(event) {
    if (event.key === "Escape") {
      event.preventDefault()
      this.cancel()
    } else if (event.key === "Enter" && event.target.tagName !== "TEXTAREA") {
      event.preventDefault()
      this.save()
    }
  }

  async save() {
    const form = this.formTarget
    const data = {}

    form.querySelectorAll("input, textarea").forEach(el => {
      if (el.name) {
        data[el.name] = el.value
      }
    })

    const csrfToken = document.querySelector("meta[name=csrf-token]")?.content

    try {
      const response = await fetch(this.urlValue, {
        method: this.methodValue.toUpperCase(),
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
          "X-CSRF-Token": csrfToken
        },
        body: JSON.stringify(data)
      })

      if (response.ok) {
        const result = await response.json()

        // Update display content
        this.displayTarget.innerHTML = result.display_html

        // Update URL to patch for future edits (in case this was a create)
        if (result.url) {
          this.urlValue = result.url
          this.methodValue = "patch"
        }

        // Update default values so cancel reflects new state
        form.querySelectorAll("input[type=text]").forEach(input => {
          input.defaultValue = input.value
        })
        form.querySelectorAll("textarea").forEach(ta => {
          ta.defaultValue = ta.value
        })

        this.formTarget.classList.add("hidden")
        this.displayTarget.classList.remove("hidden")

        // Update totals on the page
        this.updateTotals()
      } else {
        const result = await response.json()
        console.error("Save failed:", result.errors)
      }
    } catch (error) {
      console.error("Save error:", error)
    }
  }

  updateTotals() {
    // Find the table this cell belongs to
    const table = this.element.closest("table")
    if (!table) return

    const rows = table.querySelectorAll("tbody tr")
    const dayCount = 7
    const dayTotals = new Array(dayCount).fill(0)

    rows.forEach(row => {
      if (row.classList.contains("bg-gray-100")) return // skip totals row

      const cells = row.querySelectorAll("td")
      if (cells.length < dayCount + 2) return // name + 7 days + total

      let rowTotal = 0
      for (let i = 0; i < dayCount; i++) {
        const cell = cells[i + 1] // skip name column
        const display = cell.querySelector("[data-cell-edit-target='display']")
        if (!display) continue

        // Look for a currency span (has class 'currency')
        const currencySpan = display.querySelector(".currency") || display.querySelector("span.currency")
        if (currencySpan) {
          const text = currencySpan.textContent.replace(/[£,\s\u00A3]/g, "")
          const pence = Math.round(parseFloat(text) * 100)
          if (!isNaN(pence)) {
            rowTotal += pence
            dayTotals[i] += pence
          }
        }
      }

      // Update row total (last cell)
      const totalCell = cells[cells.length - 1]
      if (totalCell && rowTotal > 0) {
        totalCell.innerHTML = this.formatCurrency(rowTotal)
      } else if (totalCell) {
        totalCell.innerHTML = ""
      }
    })

    // Update day totals row
    const totalsRow = table.querySelector("tbody tr.bg-gray-700")
    if (totalsRow) {
      const cells = totalsRow.querySelectorAll("td")
      let grandTotal = 0
      for (let i = 0; i < dayCount; i++) {
        const cell = cells[i + 1]
        if (cell) {
          if (dayTotals[i] > 0) {
            cell.innerHTML = this.formatCurrency(dayTotals[i])
          } else {
            cell.innerHTML = '<span class="text-gray-300">0</span>'
          }
          grandTotal += dayTotals[i]
        }
      }
      // Grand total
      const grandCell = cells[cells.length - 1]
      if (grandCell) {
        grandCell.innerHTML = this.formatCurrency(grandTotal)
      }
    }
  }

  formatCurrency(pence) {
    const pounds = pence / 100
    const formatted = pence % 100 === 0
      ? `${pounds.toFixed(0)}`
      : `${pounds.toFixed(2)}`
    return `<span class="currency positive">${formatted}</span>`
  }
}
